import { useState, useEffect } from "react";
import { getAdminStats, getAdminUsers, getAdminListings, verifyListing, rejectListing, adminSetRole, getAdminTransactions, photoUrl } from "../lib/api";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
  Users, Home, CreditCard, CheckCircle, Shield, MessageSquare, Clock, XCircle, AlertCircle, MapPin, Bed, Bath, DollarSign
} from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const refresh = async () => {
    try {
      const [sRes, uRes, pRes, aRes, tRes] = await Promise.all([
        getAdminStats(),
        getAdminUsers(),
        getAdminListings("pending"),
        getAdminListings(),
        getAdminTransactions(),
      ]);
      setStats(sRes.data);
      setUsers(uRes.data);
      setPending(pRes.data);
      setAllListings(aRes.data);
      setTransactions(tRes.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, []);

  const handleApprove = async (id) => {
    try {
      await verifyListing(id);
      setPending((prev) => prev.filter((l) => l.listing_id !== id));
      setAllListings((prev) => prev.map((l) => (l.listing_id === id ? { ...l, verified: true } : l)));
      setStats((s) => s ? { ...s, total_listings: (s.total_listings || 0) + 0 } : s);
    } catch (err) { console.error(err); }
  };

  const openReject = (listing) => {
    setRejectTarget(listing);
    setRejectReason("");
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await rejectListing(rejectTarget.listing_id, rejectReason || "It didn't meet our quality guidelines.");
      setPending((prev) => prev.filter((l) => l.listing_id !== rejectTarget.listing_id));
      setAllListings((prev) => prev.map((l) => (l.listing_id === rejectTarget.listing_id ? { ...l, active: false } : l)));
      setRejectTarget(null);
    } catch (err) { console.error(err); }
    setRejecting(false);
  };

  const handleSetRole = async (userId, role) => {
    try {
      await adminSetRole(userId, role);
      setUsers((prev) => prev.map((u) => (u.user_id === userId ? { ...u, role } : u)));
    } catch (err) { console.error(err); }
  };

  // Recent signups (last 20 users, sorted by created_at desc)
  const recentSignups = [...users]
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    .slice(0, 20);

  const relativeTime = (iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-[#1a6b3c] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0d0d0d] font-['Fraunces']" data-testid="admin-dashboard-heading">
            Admin Dashboard
          </h1>
          {pending.length > 0 && (
            <Badge
              className="bg-amber-100 text-amber-900 border-0 rounded-full px-3 py-1.5 text-xs font-semibold"
              data-testid="admin-pending-count"
            >
              <Clock className="w-3 h-3 mr-1" /> {pending.length} listing{pending.length === 1 ? "" : "s"} awaiting review
            </Badge>
          )}
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            {[
              { icon: Users, label: "Total Users", value: stats.total_users, color: "#1a6b3c" },
              { icon: Home, label: "Active Listings", value: stats.total_listings, color: "#0d0d0d" },
              { icon: CreditCard, label: "Paid Transactions", value: stats.paid_transactions, color: "#1a6b3c" },
              { icon: MessageSquare, label: "Messages", value: stats.total_messages, color: "#0d0d0d" },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-2xl p-5 border border-[#d5d3ce]/60 shadow-sm">
                <stat.icon className="w-6 h-6 mb-2" style={{ color: stat.color }} />
                <p className="text-2xl font-bold text-[#0d0d0d]">{stat.value}</p>
                <p className="text-xs text-[#5a5a5a]">{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="bg-white border border-[#d5d3ce]/60 rounded-xl p-1 flex-wrap h-auto">
            <TabsTrigger value="pending" className="rounded-lg data-[state=active]:bg-[#1a6b3c] data-[state=active]:text-white relative" data-testid="admin-tab-pending">
              <Clock className="w-4 h-4 mr-1" /> Pending Review
              {pending.length > 0 && (
                <span className="ml-2 bg-amber-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">
                  {pending.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="signups" className="rounded-lg data-[state=active]:bg-[#1a6b3c] data-[state=active]:text-white" data-testid="admin-tab-signups">
              <Users className="w-4 h-4 mr-1" /> Recent Signups
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-[#1a6b3c] data-[state=active]:text-white" data-testid="admin-tab-users">
              <Shield className="w-4 h-4 mr-1" /> All Users
            </TabsTrigger>
            <TabsTrigger value="listings" className="rounded-lg data-[state=active]:bg-[#1a6b3c] data-[state=active]:text-white" data-testid="admin-tab-listings">
              <Home className="w-4 h-4 mr-1" /> All Listings
            </TabsTrigger>
            <TabsTrigger value="transactions" className="rounded-lg data-[state=active]:bg-[#1a6b3c] data-[state=active]:text-white" data-testid="admin-tab-transactions">
              <CreditCard className="w-4 h-4 mr-1" /> Transactions
            </TabsTrigger>
          </TabsList>

          {/* PENDING REVIEW */}
          <TabsContent value="pending">
            {pending.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 border border-[#d5d3ce]/60 text-center" data-testid="admin-pending-empty">
                <CheckCircle className="w-10 h-10 text-[#1a6b3c] mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-[#0d0d0d] font-['Fraunces']">You're all caught up</h3>
                <p className="text-[#5a5a5a] text-sm mt-1">No listings are waiting for review right now.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-pending-list">
                {pending.map((l) => (
                  <div
                    key={l.listing_id}
                    className="bg-white rounded-2xl border-2 border-amber-200 p-5 shadow-sm"
                    data-testid={`pending-listing-${l.listing_id}`}
                  >
                    {l.photos?.length > 0 && (
                      <div className="flex gap-2 overflow-x-auto mb-3" data-testid={`pending-photos-${l.listing_id}`}>
                        {l.photos.map((p) => (
                          <img key={p} src={photoUrl(p)} alt="" className="w-24 h-20 rounded-lg object-cover flex-shrink-0" />
                        ))}
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[#0d0d0d] font-['Fraunces'] text-lg truncate">{l.title}</p>
                        <p className="text-xs text-[#888]">
                          Submitted by <span className="font-semibold text-[#0d0d0d]">{l.landlord_name || "—"}</span> · {relativeTime(l.created_at)}
                        </p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-900 border-0 rounded-full text-xs shrink-0">
                        Pending
                      </Badge>
                    </div>

                    <p className="text-sm text-[#5a5a5a] mb-4 line-clamp-2">{l.description}</p>

                    <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                      <div className="flex items-center gap-1.5 text-[#5a5a5a]">
                        <MapPin className="w-3.5 h-3.5" /> {l.city}, {l.state}
                      </div>
                      <div className="flex items-center gap-1.5 text-[#1a6b3c] font-semibold">
                        <DollarSign className="w-3.5 h-3.5" /> {Number(l.price).toLocaleString()}/mo
                      </div>
                      <div className="flex items-center gap-1.5 text-[#5a5a5a]">
                        <Bed className="w-3.5 h-3.5" /> {l.bedrooms} bed
                      </div>
                      <div className="flex items-center gap-1.5 text-[#5a5a5a]">
                        <Bath className="w-3.5 h-3.5" /> {l.bathrooms} bath
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleApprove(l.listing_id)}
                        className="flex-1 rounded-full bg-[#1a6b3c] hover:bg-[#15562f] text-white text-xs font-semibold"
                        data-testid={`approve-listing-${l.listing_id}`}
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve & Publish
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openReject(l)}
                        className="rounded-full text-xs border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`reject-listing-${l.listing_id}`}
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                      <a
                        href={`/listing/${l.listing_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#5a5a5a] hover:text-[#1a6b3c] font-semibold flex items-center px-3"
                      >
                        View
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* RECENT SIGNUPS */}
          <TabsContent value="signups">
            <div className="bg-white rounded-2xl border border-[#d5d3ce]/60 overflow-hidden" data-testid="admin-signups-list">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#faf8f3]">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Name</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Email</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Role</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Verified</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Signed up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentSignups.map((u) => (
                      <tr key={u.user_id} className="border-t border-[#e5e3de]" data-testid={`signup-row-${u.user_id}`}>
                        <td className="px-4 py-3 font-medium text-[#0d0d0d]">{u.name || "—"}</td>
                        <td className="px-4 py-3 text-[#5a5a5a]">{u.email}</td>
                        <td className="px-4 py-3">
                          <Badge className="text-xs capitalize bg-[#faf8f3] text-[#0d0d0d] border-0 rounded-full">
                            {u.role || "not set"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {u.email_verified ? (
                            <CheckCircle className="w-4 h-4 text-[#1a6b3c]" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-[#5a5a5a] text-xs">{relativeTime(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* ALL USERS */}
          <TabsContent value="users">
            <div className="bg-white rounded-2xl border border-[#d5d3ce]/60 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#faf8f3]">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Name</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Email</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Role</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">BG Check</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-t border-[#e5e3de]">
                        <td className="px-4 py-3 font-medium text-[#0d0d0d]">{u.name || "—"}</td>
                        <td className="px-4 py-3 text-[#5a5a5a]">{u.email}</td>
                        <td className="px-4 py-3">
                          <Badge className="text-xs capitalize bg-[#faf8f3] text-[#0d0d0d] border-0 rounded-full">{u.role || "none"}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          {u.background_check_status === "completed" ? (
                            <CheckCircle className="w-4 h-4 text-[#1a6b3c]" />
                          ) : (
                            <span className="text-[#5a5a5a]">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#888]">—</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* ALL LISTINGS */}
          <TabsContent value="listings">
            <div className="bg-white rounded-2xl border border-[#d5d3ce]/60 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#faf8f3]">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Title</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">City</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Price</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Type</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Status</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allListings.map((l) => (
                      <tr key={l.listing_id} className="border-t border-[#e5e3de]">
                        <td className="px-4 py-3 font-medium text-[#0d0d0d]">{l.title}</td>
                        <td className="px-4 py-3 text-[#5a5a5a]">{l.city}</td>
                        <td className="px-4 py-3 text-[#1a6b3c] font-medium">${Number(l.price).toLocaleString()}</td>
                        <td className="px-4 py-3 capitalize">{l.property_type}</td>
                        <td className="px-4 py-3">
                          {!l.active ? (
                            <Badge className="text-xs bg-red-100 text-red-700 border-0 rounded-full">Rejected</Badge>
                          ) : l.verified ? (
                            <Badge className="text-xs bg-[#1a6b3c]/10 text-[#1a6b3c] border-0 rounded-full">Live</Badge>
                          ) : (
                            <Badge className="text-xs bg-amber-100 text-amber-900 border-0 rounded-full">Pending</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {!l.verified && l.active && (
                            <div className="flex gap-1">
                              <Button size="sm" onClick={() => handleApprove(l.listing_id)} className="rounded-full bg-[#1a6b3c] text-white text-xs">
                                <CheckCircle className="w-3 h-3 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openReject(l)} className="rounded-full text-xs border-red-300 text-red-600">
                                Reject
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* TRANSACTIONS */}
          <TabsContent value="transactions">
            <div className="bg-white rounded-2xl border border-[#d5d3ce]/60 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#faf8f3]">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">ID</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Type</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Amount</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Status</th>
                      <th className="text-left px-4 py-3 font-medium text-[#5a5a5a]">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t) => (
                      <tr key={t.transaction_id} className="border-t border-[#e5e3de]">
                        <td className="px-4 py-3 font-mono text-xs text-[#5a5a5a]">{t.transaction_id}</td>
                        <td className="px-4 py-3 capitalize">{t.payment_type}</td>
                        <td className="px-4 py-3 font-medium text-[#0d0d0d]">${t.amount}</td>
                        <td className="px-4 py-3">
                          <Badge className={`text-xs rounded-full border-0 ${
                            t.payment_status === "paid" ? "bg-[#1a6b3c]/10 text-[#1a6b3c]" : "bg-[#e5e3de] text-[#5a5a5a]"
                          }`}>{t.payment_status}</Badge>
                        </td>
                        <td className="px-4 py-3 text-[#5a5a5a]">{new Date(t.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(open) => !open && setRejectTarget(null)}>
        <DialogContent className="bg-white rounded-2xl max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Fraunces'] text-xl">Reject listing?</DialogTitle>
            <DialogDescription>
              We'll email the landlord with your reason so they can fix it and resubmit.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Reason</label>
            <Input
              data-testid="reject-reason-input"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Photos are blurry, price seems off, missing lease terms"
              className="rounded-xl border-black/[0.1] h-11"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setRejectTarget(null)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={submitReject}
              disabled={rejecting}
              className="rounded-xl bg-red-600 hover:bg-red-700 text-white"
              data-testid="reject-submit-btn"
            >
              {rejecting ? "Rejecting..." : "Reject Listing"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
